/**
 * Voice Configuration Service
 * Enterprise-grade service for managing TTS voice configurations.
 * 
 * @version 1.0.0
 * @author JGS Tech Team
 */

import api from '../helpers/axios';
import type { 
  VoiceConfiguration, 
  VoiceConfigurationPayload, 
  VoiceSearchOptions,
  TTSProvider 
} from '../types/aiTypes';

// API base path for voice configuration endpoints
const API_BASE = '/api/ai/voices';

/**
 * Response wrapper for list operations.
 */
interface VoiceListResponse {
  success: boolean;
  count: number;
  voices: VoiceConfiguration[];
  message?: string;
}

/**
 * Response wrapper for single voice operations.
 */
interface VoiceSingleResponse {
  success: boolean;
  voice: VoiceConfiguration;
  message?: string;
}

/**
 * Voice options for dropdowns.
 */
interface VoiceOptionsResponse {
  success: boolean;
  providers: string[];
  languages: Array<{ code: string; name: string }>;
  categories: string[];
  genders: string[];
}

/**
 * Voice Configuration Service
 * Provides CRUD operations and search functionality for voice configurations.
 */
class VoiceConfigurationService {
  /**
   * Get all active voice configurations.
   */
  async getAllActiveVoices(): Promise<VoiceConfiguration[]> {
    try {
      const response = await api.get<VoiceConfiguration[] | VoiceListResponse>(API_BASE);
      // Handle both direct array response and wrapped response
      return Array.isArray(response.data) ? response.data : (response.data.voices || []);
    } catch (error) {
      console.error('Error fetching active voices:', error);
      throw error;
    }
  }

  /**
   * Get all voice configurations including inactive.
   */
  async getAllVoices(): Promise<VoiceConfiguration[]> {
    try {
      const response = await api.get<VoiceConfiguration[] | VoiceListResponse>(`${API_BASE}/all`);
      // Handle both direct array response and wrapped response
      return Array.isArray(response.data) ? response.data : (response.data.voices || []);
    } catch (error) {
      console.error('Error fetching all voices:', error);
      throw error;
    }
  }

  /**
   * Get voice configuration by ID.
   */
  async getVoiceById(voiceId: number): Promise<VoiceConfiguration | null> {
    try {
      const response = await api.get<VoiceConfiguration | VoiceSingleResponse>(`${API_BASE}/${voiceId}`);
      // Handle both direct object response and wrapped response
      if ('voiceId' in response.data) {
        return response.data as VoiceConfiguration;
      }
      return (response.data as VoiceSingleResponse).voice;
    } catch (error) {
      console.error(`Error fetching voice ${voiceId}:`, error);
      throw error;
    }
  }

  /**
   * Get voices by provider.
   */
  async getVoicesByProvider(provider: TTSProvider): Promise<VoiceConfiguration[]> {
    try {
      const response = await api.get<VoiceConfiguration[] | VoiceListResponse>(`${API_BASE}/provider/${provider}`);
      return Array.isArray(response.data) ? response.data : (response.data.voices || []);
    } catch (error) {
      console.error(`Error fetching voices for provider ${provider}:`, error);
      throw error;
    }
  }

  /**
   * Get voices by language.
   */
  async getVoicesByLanguage(languageCode: string): Promise<VoiceConfiguration[]> {
    try {
      const response = await api.get<VoiceConfiguration[] | VoiceListResponse>(`${API_BASE}/language/${languageCode}`);
      return Array.isArray(response.data) ? response.data : (response.data.voices || []);
    } catch (error) {
      console.error(`Error fetching voices for language ${languageCode}:`, error);
      throw error;
    }
  }

  /**
   * Create a new voice configuration.
   */
  async createVoice(payload: VoiceConfigurationPayload): Promise<VoiceConfiguration> {
    try {
      const response = await api.post<VoiceConfiguration | VoiceSingleResponse>(API_BASE, payload);
      // Handle both direct object response and wrapped response
      if ('voiceId' in response.data) {
        return response.data as VoiceConfiguration;
      }
      return (response.data as VoiceSingleResponse).voice;
    } catch (error) {
      console.error('Error creating voice configuration:', error);
      throw error;
    }
  }

  /**
   * Update an existing voice configuration.
   */
  async updateVoice(voiceId: number, payload: VoiceConfigurationPayload): Promise<VoiceConfiguration> {
    try {
      const response = await api.put<VoiceConfiguration | VoiceSingleResponse>(`${API_BASE}/${voiceId}`, payload);
      // Handle both direct object response and wrapped response
      if ('voiceId' in response.data) {
        return response.data as VoiceConfiguration;
      }
      return (response.data as VoiceSingleResponse).voice;
    } catch (error) {
      console.error(`Error updating voice ${voiceId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a voice configuration.
   */
  async deleteVoice(voiceId: number): Promise<void> {
    try {
      await api.delete(`${API_BASE}/${voiceId}`);
    } catch (error) {
      console.error(`Error deleting voice ${voiceId}:`, error);
      throw error;
    }
  }

  /**
   * Set a voice as the default for its provider and language.
   */
  async setAsDefault(voiceId: number): Promise<VoiceConfiguration> {
    try {
      const response = await api.post<VoiceConfiguration | VoiceSingleResponse>(`${API_BASE}/${voiceId}/set-default`);
      // Handle both direct object response and wrapped response
      if ('voiceId' in response.data) {
        return response.data as VoiceConfiguration;
      }
      return (response.data as VoiceSingleResponse).voice;
    } catch (error) {
      console.error(`Error setting voice ${voiceId} as default:`, error);
      throw error;
    }
  }

  /**
   * Get voice options for dropdown selectors.
   */
  async getVoiceOptions(): Promise<VoiceOptionsResponse> {
    try {
      const response = await api.get<VoiceOptionsResponse>(`${API_BASE}/options`);
      return response.data;
    } catch (error) {
      console.error('Error fetching voice options:', error);
      throw error;
    }
  }

  /**
   * Search voices with filters.
   */
  async searchVoices(options: VoiceSearchOptions): Promise<VoiceConfiguration[]> {
    try {
      const params = new URLSearchParams();
      
      if (options.providers?.length) {
        params.set('providers', options.providers.join(','));
      }
      if (options.languages?.length) {
        params.set('languages', options.languages.join(','));
      }
      if (options.categories?.length) {
        params.set('categories', options.categories.join(','));
      }
      if (options.genders?.length) {
        params.set('genders', options.genders.join(','));
      }
      if (options.isActive !== undefined) {
        params.set('isActive', String(options.isActive));
      }

      const response = await api.get<VoiceConfiguration[] | VoiceListResponse>(
        `${API_BASE}/search?${params.toString()}`
      );
      return Array.isArray(response.data) ? response.data : (response.data.voices || []);
    } catch (error) {
      console.error('Error searching voices:', error);
      throw error;
    }
  }

  /**
   * Test a voice by generating sample audio.
   */
  async testVoice(voiceId: number, testText?: string): Promise<{ audioUrl: string }> {
    try {
      const response = await api.post<{ success: boolean; audioUrl: string }>(
        `${API_BASE}/${voiceId}/test`,
        { text: testText || 'This is a sample test of the voice configuration.' }
      );
      return { audioUrl: response.data.audioUrl };
    } catch (error) {
      console.error(`Error testing voice ${voiceId}:`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const voiceConfigurationService = new VoiceConfigurationService();
export default voiceConfigurationService;
